import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';
import { formatDateDisplay } from '../../utils/dateUtils';

// Font: Roboto (Cần thiết để hiển thị Tiếng Việt)
Font.register({
    family: 'Roboto',
    fonts: [
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf' },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf', fontWeight: 'bold' },
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-italic-webfont.ttf', fontStyle: 'italic' }
    ]
});

// --- STYLES (Cập nhật theo style của Invoice) ---
const styles = StyleSheet.create({
    page: { padding: 30, fontSize: 10, fontFamily: 'Roboto', color: '#333' },
    
    // Header Section (Logo + Info)
    header: { 
        flexDirection: 'row', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 10, 
        borderBottomWidth: 2, 
        borderBottomColor: '#007373', 
        paddingBottom: 15 
    },
    headerLeft: { 
        width: '40%', // Tăng chiều rộng cột chứa logo
        justifyContent: 'center', // Căn logo lên trên cùng
        alignItems: 'flex-start' 
    },
    headerRight: { 
        width: '60%', 
        justifyContent: 'center',
        alignItems: 'flex-end', 
        textAlign: 'right' 
    },
    logoImage: { width: 'auto', height: 70, objectFit: 'contain' },
    companyInfo: { textAlign: 'right', fontSize: 9, color: '#555' },
    companyName: { fontSize: 14, fontWeight: 'bold', color: '#007373', marginBottom: 4 },

    // Report Title & Meta
    reportTitle: { fontSize: 18, textAlign: 'center', fontWeight: 'bold', marginTop: 10, marginBottom: 5, textTransform: 'uppercase' },
    reportPeriod: { fontSize: 11, textAlign: 'center', marginBottom: 5, fontStyle: 'italic' },
    reportDate: { fontSize: 10, textAlign: 'center', marginBottom: 20, color: '#777' },

    // Table Styles
    table: { display: "table", width: "auto", borderStyle: "solid", borderRightWidth: 0, borderBottomWidth: 0 },
    tableRow: { margin: "auto", flexDirection: "row", borderBottomWidth: 1, borderBottomColor: '#eee', minHeight: 24, alignItems: 'center' },
    tableHeaderRow: { margin: "auto", flexDirection: "row", backgroundColor: '#f2f2f2', borderBottomWidth: 1, borderBottomColor: '#999', minHeight: 24, alignItems: 'center' },
    tableCol: { borderStyle: "solid", borderLeftWidth: 1, borderLeftColor: '#eee', borderTopWidth: 0, padding: 4 }, // Bỏ border top để tránh nét đôi
    tableCell: { fontSize: 9 },
    tableHeaderCell: { fontSize: 9, fontWeight: 'bold' },

    tableSummaryRow: { margin: "auto", flexDirection: "row", backgroundColor: '#e0f2f1', borderBottomWidth: 1, borderBottomColor: '#999', minHeight: 24, alignItems: 'center' },
    tableSummaryCell: { fontSize: 9, fontWeight: 'bold', color: '#007373' },

    // Summary
    summary: { marginTop: 20, textAlign: 'right', fontSize: 12, fontWeight: 'bold', color: '#007373' }
});

const normalizeColumns = (columns) => {
    if (!columns || columns.length === 0) return [];
    
    const totalDefinedWidth = columns.reduce((acc, col) => acc + (parseFloat(col.width) || 0), 0);
    
    return columns.map(col => ({
        ...col,
        width: totalDefinedWidth > 0 
            ? `${(parseFloat(col.width || 0) / totalDefinedWidth * 100).toFixed(2)}%` 
            : `${(100 / columns.length).toFixed(2)}%`
    }));
};

// --- COMPONENT PDF DOCUMENT ---
const ReportPdf = ({ title, dateRange, columns, data, summary, summaryRow }) => {
    const pdfColumns = normalizeColumns(columns);
    const periodStr = dateRange.start && dateRange.end 
        ? `Giai đoạn: ${formatDateDisplay(dateRange.start)} - ${formatDateDisplay(dateRange.end)}`
        : 'Giai đoạn: Toàn thời gian';
    const printDate = `Ngày xuất báo cáo: ${new Date().toLocaleDateString('vi-VN')}`;
    const logoUrl = typeof window !== 'undefined' ? `${window.location.origin}/fola-travel-logo-with-slogan.png` : '';

    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                
                {/* 1. Header */}
                <View style={styles.header}>
                <View style={styles.headerLeft}>
                    {logoUrl && <Image style={styles.logoImage} src={logoUrl} />}
                </View>
                <View style={styles.headerRight}>
                    <Text style={styles.companyInfo}>Fola Travel and Services Co.Ltd</Text>
                    <Text style={styles.companyInfo}>Pavilion P2, Vinhomes Ocean Park, Gia Lam, Hanoi</Text>
                    <Text style={styles.companyInfo}>T: +84 986 332 366 | E: folatravel@gmail.com</Text>
                    <Text style={styles.companyInfo}>W: www.folatravel.com</Text>
                </View>
                </View>

                {/* 2. Tiêu đề báo cáo */}
                <Text style={styles.reportTitle}>{title}</Text>
                <Text style={styles.reportPeriod}>{periodStr}</Text>
                <Text style={styles.reportDate}>{printDate}</Text>
                
                {/* 3. Bảng Dữ liệu */}
                <View style={styles.table}>
                    {/* Header Row */}
                    <View style={styles.tableHeaderRow} fixed>
                        {pdfColumns.map((col, i) => (
                            <View key={i} style={{ ...styles.tableCol, width: col.width }}>
                                <Text style={styles.tableHeaderCell}>{col.header}</Text>
                            </View>
                        ))}
                    </View>

                    {/* Body Rows */}
                    {data.map((row, i) => (
                        <View key={i} style={styles.tableRow} wrap={false}> 
                            {pdfColumns.map((col, j) => (
                                <View key={j} style={{ ...styles.tableCol, width: col.width }}>
                                    <Text style={styles.tableCell}>
                                        {col.pdfRender 
                                            ? col.pdfRender(row) 
                                            : (typeof col.render === 'function' 
                                                ? (typeof col.render(row) === 'object' ? row[col.accessor] : col.render(row)) // Nếu render trả về Object (Component) thì fallback về raw accessor
                                                : (row[col.accessor] || ''))
                                        }
                                    </Text>
                                </View>
                            ))}
                        </View>
                    ))}

                    {/* Dòng Tổng cộng trong PDF */}
                    {summaryRow && (
                        <View style={styles.tableSummaryRow} wrap={false}> 
                            {pdfColumns.map((col, j) => (
                                <View key={j} style={{ ...styles.tableCol, width: col.width }}>
                                    <Text style={styles.tableSummaryCell}>
                                        {summaryRow[col.accessor] || ''}
                                    </Text>
                                </View>
                            ))}
                        </View>
                    )}
                </View>

                {/* 4. Tổng kết */}
                {summary && (
                    <View style={styles.summary} wrap={false}>
                        <Text>{summary}</Text>
                    </View>
                )}
            </Page>
        </Document>
    );
};

const ReportExportDialog = ({ open, onClose, data = [], dateRange = {}, customColumns = [], summaryRow = null, fileNamePrefix = 'BaoCao', customTitle = '' }) => {
    const [reportTitle, setReportTitle] = useState(customTitle);
    const [previewData, setPreviewData] = useState([]);
    const [fileName, setFileName] = useState('');

    useEffect(() => {
        if (open) {
            setReportTitle(customTitle);
            setPreviewData(data);

            const cleanDate = (d) => d ? formatDateDisplay(d).replace(/\//g, '-') : 'ALL';
            const fStart = cleanDate(dateRange.start);
            const fEnd = cleanDate(dateRange.end);
            const fToday = new Date().toLocaleDateString('vi-VN').replace(/\//g, '-');
            
            setFileName(`${fileNamePrefix}_${fStart}_${fEnd}_${fToday}.pdf`);
        }
    }, [open, data, dateRange, customTitle, fileNamePrefix]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Xuất Báo Cáo</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 2 }}>
                    <TextField fullWidth label="Tiêu đề hiển thị" value={reportTitle} onChange={(e) => setReportTitle(e.target.value)} />
                </Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Xem trước dữ liệu ({previewData.length} dòng):</Typography>
                
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {customColumns.map((c, i) => <TableCell key={i}>{c.header}</TableCell>)}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {previewData.slice(0, 10).map((row, i) => (
                                <TableRow key={i}>
                                    {customColumns.map((c, j) => (
                                        <TableCell key={j}>
                                            {c.render ? c.render(row) : (row[c.accessor] || '')}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {/* Thêm dòng tổng vào UI Bảng xem trước */}
                            {summaryRow && (
                                <TableRow sx={{ bgcolor: '#e0f2f1', '& td': { fontWeight: 'bold', color: '#007373' } }}>
                                    {customColumns.map((c, j) => (
                                        <TableCell key={j}>
                                            {summaryRow[c.accessor] || ''}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                {previewData.length > 10 && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>... và {previewData.length - 10} dòng khác</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Đóng</Button>
                <PDFDownloadLink
                    document={
                        <ReportPdf 
                            title={reportTitle} 
                            dateRange={dateRange}
                            columns={customColumns}
                            data={previewData} 
                            summaryRow={summaryRow} // Truyền xuống component PDF
                            summary={`Tổng số dòng: ${previewData.length}`} 
                        />
                    }
                    fileName={fileName}
                    style={{ textDecoration: 'none' }}
                >
                    {({ loading }) => (
                        <Button variant="contained" disabled={loading} sx={{ bgcolor: '#007373' }}>
                            {loading ? 'Đang tạo...' : 'Tải xuống PDF'}
                        </Button>
                    )}
                </PDFDownloadLink>
            </DialogActions>
        </Dialog>
    );
};

export default ReportExportDialog;